"use client";

import React, { useState } from "react";

function renderInlineMarkdown(text) {
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
        }

        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
}

function renderMarkdownContent(content) {
    const lines = content.trim().split("\n");
    const elements = [];
    let bulletItems = [];

    const flushBullets = () => {
        if (bulletItems.length > 0) {
            elements.push(
                <ul key={`list-${elements.length}`} className="list-disc pl-5 my-3 space-y-1">
                    {bulletItems.map((item, index) => (
                        <li key={`${item}-${index}`}>{renderInlineMarkdown(item)}</li>
                    ))}
                </ul>
            );
            bulletItems = [];
        }
    };

    lines.forEach((line) => {
        const trimmedLine = line.trim();

        if (!trimmedLine) {
            flushBullets();
            return;
        }

        if (trimmedLine.startsWith("## ")) {
            flushBullets();
            elements.push(
                <h3 key={`heading-${elements.length}`} className="mt-2 mb-2 text-lg font-bold">
                    {renderInlineMarkdown(trimmedLine.replace(/^##\s+/, ""))}
                </h3>
            );
            return;
        }

        if (trimmedLine.startsWith("-   ") || trimmedLine.startsWith("- ")) {
            bulletItems.push(trimmedLine.replace(/^-+\s+/, ""));
            return;
        }

        flushBullets();
        elements.push(
            <p key={`paragraph-${elements.length}`} className="my-3 leading-7">
                {renderInlineMarkdown(trimmedLine)}
            </p>
        );
    });

    flushBullets();

    return elements;
}

const overlays = [
    {
        buttonTitle: "Start Here",
        title: "Start Here",
        content:
            `This plan is built around one simple goal:

**Stay consistent, build gradually, and arrive healthy.**

You do **not** need speed work, intervals, or hard workouts to finish a first half marathon. For most beginners, the biggest win comes from:

-   running consistently
-   keeping most runs easy
-   slowly extending the long run
-   avoiding the kind of training that leads to soreness, missed sessions, or injury

Even though the plan can work with only **3 running days per week**, people usually feel better prepared when they also do some of the optional work:

-   **1 optional extra easy run**
-   **up to 2 optional cross-training days**
-   **1 full rest day**

The extra work should help you feel stronger and more comfortable, not more beat up.`,
    },
    {
        buttonTitle: "Effort Guide",
        title: "Keep Most Runs Comfortable",
        content:
            `All runs should be done at an **easy, conversational effort**.

That means:

-   you should be able to talk in short sentences
-   you should feel like you could keep going a bit longer
-   you should finish most runs feeling controlled, not crushed

Walking is completely fine, especially early on or during long runs.

A good rule:  
**run easy enough that you can stay consistent week after week.**`,
    },
    {
        buttonTitle: "Optional Days",
        title: "How to Use Them",
        content:
            `## **Optional 4th running day**

This should be a **short, easy run only**.

Use it to:

-   build comfort with running more often
-   improve endurance gradually
-   make the plan feel easier overall

Do **not** turn it into a hard workout.

## **Optional cross-training days**

You can use one of the many programs we have in the Health Hub to complement your running.
These can be:

-   Walking
-   Even a 30-minute brisk walk on an optional day helps recovery more than sitting.
-   cycling
-   swimming
-   elliptical
-   easy hiking
-   strength training
-   mobility work

The goal is to support your running, not compete with it.

Good cross-training should leave you feeling:

-   looser
-   stronger
-   more energized

Not exhausted.`,
    },
    {
        buttonTitle: "Injury Prevention",
        title: "Injury Prevention and Consistency",
        content:
            `## **1. Follow the plan closely**

Try to follow the plan as closely as possible. The real benefit comes from stacking week after week of steady training.

Consistency does three things beginners need most:

-   It teaches your tendons, bones, and feet to handle repeated impact. That is what prevents injury, not stretching alone.
-   It builds aerobic fitness without the fatigue spikes that speed sessions create.
-   It gives you psychological momentum. Missing one core run occasionally is fine. Missing two weeks in a row resets adaptation.

## **2. Do not chase missed workouts**

If you miss a run, do not try to cram it in later. Just continue with the plan.

## **3. Extra work is optional**

The optional run and cross-training can help a lot, but only if your body is handling them well.

Skip them when:

-   something hurts
-   you feel unusually sore
-   fatigue is building up
-   life stress is high

## **4. Healthy beats heroic**

It is better to arrive slightly undertrained than injured.`,
    },
    {
        buttonTitle: "Warm-Up",
        title: "Race-Day Warm-Up Routine",
        content:
            `This warmup will get your body ready for the race.

I've been doing it more more than 10 years with great success.

I recommend practicing it before your threshold runs.

You don't need to do any running.

This warmup is enough.`,
        videoUrl: "https://player.vimeo.com/video/845348268",
    },
    {
        buttonTitle: "Race Week",
        title: "Taper",
        content:
            `The last 2 weeks of the plan are a **taper**.  
That means the mileage drops, but the goal stays the same:

-   keep runs easy
-   stay consistent
-   prioritize sleep
-   avoid any new or hard workouts

The taper is about **letting your body recover and freshen up** so you can run the race feeling strong and controlled.`,
    },
];

export default function HalfMarathonMenu() {
    const [activeOverlay, setActiveOverlay] = useState(null);

    return (
        <div className="mb-4 p-2">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                {overlays.map((overlay, index) => (
                    <button
                        key={overlay.buttonTitle}
                        type="button"
                        onClick={() => setActiveOverlay(index)}
                        className="bg-green-500 text-white px-2 py-2 rounded-md hover:bg-green-600"
                    >
                        {overlay.buttonTitle}
                    </button>
                ))}
            </div>

            {activeOverlay !== null && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center"
                    onClick={() => setActiveOverlay(null)}
                >
                    <div
                        className="bg-white p-4 rounded-md m-4 relative max-w-2xl w-full max-h-[80vh] overflow-y-auto text-left"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveOverlay(null)}
                            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                        >
                            ✕
                        </button>

                        <h2 className="text-xl font-bold mb-3">
                            {overlays[activeOverlay].title}
                        </h2>
                        <div className="mb-0">
                            {renderMarkdownContent(overlays[activeOverlay].content)}
                        </div>

                        {overlays[activeOverlay].videoUrl && (
                            <div className="relative pb-[56.25%] overflow-hidden rounded mt-4">
                                <iframe
                                    src={overlays[activeOverlay].videoUrl}
                                    title={overlays[activeOverlay].title}
                                    className="absolute top-0 left-0 w-full h-full"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
